import { io, type Socket as SocketType } from 'socket.io-client';
import { getApiUrl } from './env';
import type { ServerToClientEvents, ClientToServerEvents, PlayerView } from '@uno-online/shared';
import { useGameStore } from '@/features/game/stores/game-store';
import { useRoomStore } from './stores/room-store';
import { useToastStore } from './stores/toast-store';
import { playSound } from './sound/sound-manager';
import { useGatewayStore, type PlayerVoicePresence } from './voice/gateway-store';
import { sendNotification } from './utils/notification';
import { useServerVersionStore } from './stores/server-version-store';
import { useServerStore } from './stores/server-store';
import { setServerTimeOffset } from './server-time';
import { useSpectatorStore } from '@/features/game/stores/spectator-store';
import { useLobbyStore } from '@/features/lobby/stores/lobby-store';
import { resetClientRoomState } from './stores/reset-room';
import { globalNavigate } from './utils/global-navigate';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { clearStoredAuthToken } from './api';

type TypedSocket = SocketType<ServerToClientEvents, ClientToServerEvents>;

let socket: TypedSocket | null = null;
let connectionStatusCallback: ((status: 'connected' | 'disconnected' | 'reconnecting') => void) | null = null;

export function onConnectionStatus(cb: (status: 'connected' | 'disconnected' | 'reconnecting') => void) {
  connectionStatusCallback = cb;
}

function currentToken(): string | null {
  return useAuthStore.getState().token ?? localStorage.getItem('token');
}

function clearAuthSession(): void {
  clearStoredAuthToken();
  useAuthStore.setState({ user: null, token: null, loading: false, initialized: true });
}

async function checkCaddyVersion(): Promise<void> {
  try {
    const res = await fetch('/healthz', { method: 'HEAD', cache: 'no-store' });
    const instanceStart = res.headers.get('X-Instance-Start');
    if (instanceStart) {
      useServerVersionStore.getState().setClientVersion(instanceStart);
    }
  } catch {
    // Caddy header unavailable (e.g. dev mode) — skip
  }
}

export function getSocket(): TypedSocket {
  if (!socket) {
    const token = currentToken();
    socket = io(getApiUrl(), {
      auth: { token },
      transports: ['websocket'],
      autoConnect: false,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });

    socket.on('room:updated', (data: Record<string, unknown>) => {
      if (data.room) {
        useRoomStore.getState().updateRoom({ room: data.room as any });
      }
      if (useGameStore.getState().ownerTransferAt !== null) {
        useGameStore.getState().setOwnerTransferAt(null);
      }
    });

    socket.on('seat:updated', (data: { seats: unknown[]; spectators: unknown[] }) => {
      useRoomStore.getState().updateSeats(data as any);
    });

    socket.on('room:ready_changed', (data) => {
      const selfId = useAuthStore.getState().user?.id ?? useGameStore.getState().viewerId;
      if (data.ready && data.playerId !== selfId) playSound('ready');
    });

    socket.on('voice:presence', (presence) => {
      const newPresence = (presence ?? {}) as Record<string, PlayerVoicePresence>;
      const oldPresence = useGatewayStore.getState().playerVoicePresence;

      const selfId = useGameStore.getState().viewerId;
      let changed = false;
      for (const [uid, p] of Object.entries(newPresence)) {
        const old = oldPresence[uid];
        if (!old || old.inVoice !== p.inVoice || old.micEnabled !== p.micEnabled || old.forceMuted !== p.forceMuted) { changed = true; }
        if (uid === selfId) continue;
        const wasInVoice = old?.inVoice;
        if (p.inVoice && !wasInVoice) playSound('voice_join');
        else if (!p.inVoice && wasInVoice) playSound('voice_leave');
      }
      for (const [uid, p] of Object.entries(oldPresence)) {
        if (!newPresence[uid]) { changed = true; }
        if (uid === selfId) continue;
        if (p.inVoice && !newPresence[uid]) playSound('voice_leave');
      }
      if (changed) useGatewayStore.getState().setPlayerVoicePresence(newPresence);
    });

    const handleGameView = (view: PlayerView) => {
      const prevPhase = useGameStore.getState().phase;
      const prevCurrentIndex = useGameStore.getState().currentPlayerIndex;

      const settings = view.settings;
      let turnEndTime: number | null = null;
      if (settings && view.phase !== 'round_end' && view.phase !== 'game_over') {
        const timeLimit = settings.houseRules?.fastMode
          ? Math.floor(settings.turnTimeLimit / 2)
          : settings.turnTimeLimit;
        turnEndTime = Date.now() + timeLimit * 1000;
      }
      useGameStore.getState().setGameState(view, turnEndTime);

      const viewerId = view.viewerId ?? useGameStore.getState().viewerId;
      const currentPlayerId = view.players[view.currentPlayerIndex]?.id;

      if (prevPhase !== 'playing' && view.phase === 'playing' && view.roundNumber === 1) {
        sendNotification('gameStart');
      }

      if (
        view.phase === 'playing' &&
        currentPlayerId === viewerId &&
        (prevPhase !== 'playing' || prevCurrentIndex !== view.currentPlayerIndex)
      ) {
        sendNotification('myTurn');
      }

      if (view.phase === 'game_over' && prevPhase !== 'game_over') {
        sendNotification('gameEnd');
      }
    };

    socket.on('game:state', (view) => {
      handleGameView(view);
      const deckHash = view.deckHash;
      if (deckHash) {
        useToastStore.getState().addToast(`Card sequence Hash: ${deckHash.slice(0, 16)}...`, 'info');
      }
    });
    socket.on('game:update', handleGameView);

    socket.on('game:next_round_vote', (vote) => {
      useGameStore.getState().setNextRoundVote(vote.votes > 0 ? vote : null);
    });

    socket.on('game:round_end', (data) => {
      if (data?.roundEndAt) {
        useGameStore.getState().setRoundEndAt(data.roundEndAt);
      }
    });

    socket.on('game:over', (data) => {
      if (data?.gameOverAt) {
        useGameStore.getState().setGameOverAt(data.gameOverAt);
      }
    });

    socket.on('game:back_to_room', (data: { seats?: unknown[]; spectators?: unknown[]; room?: unknown }) => {
      const roomCode = useRoomStore.getState().roomCode;
      if (data.seats && data.spectators && data.room && roomCode) {
        useRoomStore.getState().setRoom(roomCode, data.seats as any, data.spectators as any, data.room as any);
      }
      useGameStore.getState().clearGame();
    });

    socket.on('game:card_drawn', (data) => {
      useGameStore.getState().setDrawnCard(data.card);
    });

    socket.on('game:action_rejected', (data) => {
      useToastStore.getState().addToast(data.reason || 'Invalid operation', 'error');
      playSound('error');
    });

    socket.on('player:timeout', (_data) => {
      // noop
    });

    socket.on('player:disconnected', (data) => {
      const player = useGameStore.getState().players.find(p => p.id === data.playerId);
      if (player) useToastStore.getState().addToast(`${player.name}${player.isBot ? ' (AI)' : ''} Disconnected`, 'info');
      playSound('player_leave');
    });

    socket.on('player:reconnected', (data) => {
      const player = useGameStore.getState().players.find(p => p.id === data.playerId);
      if (player) useToastStore.getState().addToast(`${player.name}${player.isBot ? ' (AI)' : ''} reconnect`, 'success');
      playSound('player_join');
    });

    socket.on('player:autopilot', (data) => {
      const player = useGameStore.getState().players.find(p => p.id === data.playerId);
      if (player) {
        useToastStore.getState().addToast(
          data.enabled ? `${player.name}${player.isBot ? ' (AI)' : ''} Enter managed mode` : `${player.name}${player.isBot ? ' (AI)' : ''} Exit managed mode`,
          'info',
        );
      }
    });

    // The three spectator events all carry the full authoritative
    // `spectators` array per socket-events.ts; trust the contract — local
    // fallbacks would just paper over future server-side regressions.
    socket.on('room:spectator_list', (data) => {
      const store = useSpectatorStore.getState();
      store.setSpectators(data.spectators);
      const nicknameSet = new Set(data.spectators.map((s) => s.nickname));
      if (store.pendingJoinQueue.some((n) => !nicknameSet.has(n))) {
        store.setPendingJoinQueue(store.pendingJoinQueue.filter((n) => nicknameSet.has(n)));
      }
    });

    socket.on('room:spectator_joined', (data) => {
      useToastStore.getState().addToast(`${data.nickname} Start watching the game`, 'info');
      useSpectatorStore.getState().setSpectators(data.spectators);
    });

    socket.on('room:spectator_left', (data) => {
      useToastStore.getState().addToast(`${data.nickname} Leave the game`, 'info');
      useSpectatorStore.getState().setSpectators(data.spectators);
    });

    socket.on('room:owner_transfer_pending', (data) => {
      useGameStore.getState().setOwnerTransferAt(data.transferAt);
    });
    socket.on('room:owner_transfer_cancelled', () => {
      useGameStore.getState().setOwnerTransferAt(null);
    });

    socket.on('server:version', (data) => {
      useServerVersionStore.getState().setServerVersion(data.version);
      if (data.serverTime) setServerTimeOffset(data.serverTime);
    });

    socket.on('lobby:rooms', (rooms) => {
      useLobbyStore.getState().setActiveRooms(rooms);
    });

    let latencyInterval: ReturnType<typeof setInterval> | null = null;
    const measureLatency = () => {
      const start = performance.now();
      socket!.volatile.emit('ping:latency', () => {
        useServerStore.getState().setSocketLatency(Math.round(performance.now() - start));
      });
    };

    socket.on('connect', () => {
      connectionStatusCallback?.('connected');
      measureLatency();
      latencyInterval = setInterval(measureLatency, 30_000);
      checkCaddyVersion();
    });

    socket.on('disconnect', () => {
      connectionStatusCallback?.('disconnected');
      useServerStore.getState().setSocketLatency(null);
      if (latencyInterval) { clearInterval(latencyInterval); latencyInterval = null; }
    });

    socket.io.on('reconnect_attempt', () => {
      connectionStatusCallback?.('reconnecting');
    });

    socket.io.on('reconnect_failed', () => {
      connectionStatusCallback?.('disconnected');
    });

    socket.on('connect_error', (err) => {
      if (err.message === 'Authentication failed') {
        resetClientRoomState();
        clearAuthSession();
        socket?.disconnect();
        socket = null;
        window.location.href = '/?session_expired=1';
      }
    });

    socket.on('auth:kicked', (_data) => {
      resetClientRoomState();
      clearAuthSession();
      window.location.href = '/';
    });

    socket.on('game:kicked', (data) => {
      if (data.toSpectator) {
        useGameStore.getState().setSpectator(true);
        useToastStore.getState().addToast(data.reason || 'You have been moved to the spectator seat', 'info');
        return;
      }
      resetClientRoomState();
      sendNotification('kicked', data.reason || 'You have been removed from the room');
      useToastStore.getState().addToast(data.reason || 'You have been removed from the game', 'error');
      if (window.location.pathname !== '/') {
        globalNavigate('/');
      }
    });

    socket.on('game:cheat_detected', () => {
      useGameStore.getState().setCheatDetected(true);
    });

    socket.on('room:dissolved', (data) => {
      if (useGameStore.getState().cheatDetected) return;
      useGameStore.getState().setDissolvedReason(data?.reason ?? 'host_closed');
    });
  }
  return socket;
}

export function refreshVoicePresence(): void {
  const s = getSocket();
  s.emit('voice:presence:get', (presence) => {
    useGatewayStore.getState().setPlayerVoicePresence(presence as Record<string, PlayerVoicePresence>);
  });
}

export function connectSocket(): void {
  const s = getSocket();
  const token = currentToken();
  const oldToken = (s.auth as { token?: string | null } | undefined)?.token ?? null;
  s.auth = { token };
  if (s.connected && oldToken !== token) {
    s.disconnect();
    s.connect();
    return;
  }
  if (!s.connected) {
    s.connect();
  }
}

export function disconnectSocket(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}
